/** Formatting helpers local to the health dashboard. */
import { resolveHost } from '../../utils/hosts';

/** "just now" / "4s ago" / "2m ago" for the last-updated indicator. */
export function formatAgo(epochMs: number, now: number = Date.now()): string {
	const seconds = Math.max(0, Math.round((now - epochMs) / 1000));
	if (seconds < 2) return 'just now';
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	return `${Math.floor(minutes / 60)}h ago`;
}

/** "00:42" / "01:12:09" - used for broadcast elapsed time. */
export function formatElapsed(fromEpochMs: number, toEpochMs: number = Date.now()): string {
	const totalSeconds = Math.max(0, Math.floor((toEpochMs - fromEpochMs) / 1000));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;
	const pad = (n: number) => String(n).padStart(2, '0');

	return hours
		? `${hours}:${pad(minutes)}:${pad(seconds)}`
		: `${pad(minutes)}:${pad(seconds)}`;
}

/** "00:42" / "01:12:09" from a duration rather than a start time. */
export function formatDurationMs(durationMs: number): string {
	return formatElapsed(0, durationMs);
}

export function formatTimestamp(epochMs?: number): string {
	if (!epochMs) return '—';
	return new Date(epochMs).toLocaleTimeString();
}

/**
 * The self-hosted Mux analytics view for a platform event, or null when there
 * is nothing to link to.
 *
 * Production is the bare apex and every other environment suffixes it, the same
 * rule `conference-room-entry` and `@brandlive1941/showboard` build their hosts on.
 * `prod` is accepted too because showboard's `Environment` spells it that way.
 *
 * Passed through `resolveHost` so the link stays in the zone the producer is
 * already on - one backend serves every brandlive and brandtv domain, and a
 * hardcoded brandlive host sent them out of a brandtv one.
 */
export function liveMonitorUrl(eventUuid?: string | null): string | null {
	const env = process.env.REACT_APP_NODE_ENV;
	if (!eventUuid || !env || env === 'local') return null;
	const suffix = env === 'production' || env === 'prod' ? '' : `-${env.toLowerCase()}`;
	const host = resolveHost(`live-monitor.brandlive${suffix}.com`);
	// resolveHost hands back the value untouched off a localhost origin, and
	// that form carries no scheme.
	const origin = /^https?:\/\//.test(host) ? host : `https://${host}`;
	return `${origin}/?event=${encodeURIComponent(eventUuid)}`;
}

/**
 * RTMP urls carry stream keys. Show enough to identify the destination without
 * putting a usable credential on a producer's screen.
 */
export function redactStreamUrl(url: string): string {
	try {
		const parsed = new URL(url.replace(/^rtmps?:\/\//, 'https://'));
		const path = parsed.pathname.split('/').filter(Boolean);
		const app = path[0] ?? '';
		return `${parsed.host}/${app}${path.length > 1 ? '/•••' : ''}`;
	} catch {
		return url.split('/').slice(0, 3).join('/');
	}
}
